<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { Activity, AlertTriangle, ArrowRight, Bot, Check, ContactRound, Database, MessageSquareText, RefreshCw, Settings2, WalletCards } from 'lucide-vue-next'
import { api, errorText, object, rows, type JsonRecord } from '../api'
import { useRouter } from 'vue-router'

const router = useRouter()
const loading = ref(true)
const error = ref('')
const data = ref<JsonRecord>({})
const agents = ref<JsonRecord[]>([])
const contacts = ref<JsonRecord[]>([])
const libraries = ref<JsonRecord[]>([])
const contexts = ref<JsonRecord[]>([])
const metrics = ref<{ label: string; value: string; note: string; icon: typeof Activity }[]>([])
const trend = ref<{ label: string; value: number }[]>([])
const maxTrend = ref(1)

function formatPoints(value: number): string {
  if (!Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('zh-CN', { maximumFractionDigits: 0 })
}

const shortcuts = computed(() => [
  { title: 'AI 客服', note: `${agents.value.length} 个客服配置`, path: '/agents', icon: Bot },
  { title: '联系人', note: `${contacts.value.length} 条导师绑定`, path: '/contacts', icon: ContactRound },
  { title: '知识与内容', note: `${libraries.value.length} 个可用库`, path: '/knowledge', icon: Database },
  { title: '话术与阶段', note: '维护服务表达与跟进规则', path: '/scripts', icon: MessageSquareText },
  { title: '对话记忆', note: '查看客服绑定的记忆库', path: '/memories', icon: Activity },
  { title: '点数与用量', note: '查看余额、冻结和消费流水', path: '/points', icon: WalletCards }
])

async function load() {
  loading.value = true
  error.value = ''
  const results = await Promise.allSettled([
    api.get('/overview'),
    api.get('/agents'),
    api.get('/contacts'),
    api.get('/libraries'),
    api.get('/records/customer-context?limit=6'),
    api.get('/usage?limit=500')
  ])
  const failures = results.filter(result => result.status === 'rejected')
  if (failures.length) error.value = failures.map(result => errorText((result as PromiseRejectedResult).reason)).join('；')
  if (results[0].status === 'fulfilled') data.value = object(results[0].value)
  if (results[1].status === 'fulfilled') agents.value = rows(results[1].value)
  if (results[2].status === 'fulfilled') contacts.value = rows(results[2].value)
  if (results[3].status === 'fulfilled') libraries.value = rows(results[3].value)
  if (results[4].status === 'fulfilled') contexts.value = rows(results[4].value)
  // 本月点数消耗：模型用量流水里 delta 为负的条数取绝对值汇总（最多回看最近 500 条）。
  let monthConsumed = ''
  if (results[5].status === 'fulfilled') {
    const monthStart = new Date()
    monthStart.setDate(1)
    monthStart.setHours(0, 0, 0, 0)
    let used = 0
    for (const row of rows(results[5].value)) {
      if (String(row.kind ?? object(row.detail).kind) !== 'model_usage') continue
      const time = Date.parse(String(row.created_at ?? ''))
      if (Number.isNaN(time) || time < monthStart.getTime()) continue
      const delta = Number(row.delta ?? object(row.detail).delta ?? 0)
      if (delta < 0) used += -delta
    }
    monthConsumed = formatPoints(used)
  }
  const summary = object(data.value.summary ?? data.value.metrics)
  metrics.value = [
    { label: 'AI 客服', value: String(summary.agent_count ?? agents.value.length), note: '正在使用的客服配置', icon: Bot },
    { label: '已绑定联系人', value: String(summary.contact_count ?? contacts.value.length), note: '可接收导师咨询的管理员', icon: ContactRound },
    { label: '本月点数消耗', value: monthConsumed || '—', note: '按实际模型用量结算', icon: WalletCards }
  ]
  const rawTrend = rows(data.value.trend)
  trend.value = rawTrend.map(item => ({
    label: String(item.day ?? item.date ?? ''),
    value: Number(item.count ?? item.total ?? item.value ?? 0)
  }))
  maxTrend.value = Math.max(1, ...trend.value.map(item => item.value))
  loading.value = false
}

onMounted(load)
</script>

<style scoped>
/* 客服数据改为三张卡片：桌面三等分；窄屏两列、第三张通栏，保持分割线整洁。 */
.overview-stat-band { grid-template-columns: repeat(3, minmax(0, 1fr)); }
@media (max-width: 1100px) {
  .overview-stat-band { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .overview-stat:nth-child(odd) { border-left: 0; }
  .overview-stat:nth-child(3) { grid-column: 1 / -1; border-left: 0; border-top: 1px solid var(--line-light); }
}
@media (max-width: 720px) {
  .overview-stat-band { grid-template-columns: minmax(0, 1fr); }
  .overview-stat:nth-child(n + 2) { border-left: 0; border-top: 1px solid var(--line-light); }
}
</style>

<template>
  <div class="page-header">
    <div>
      <h1 class="page-title">工作概览</h1>
      <p class="page-description">查看客服运行状态、近期服务上下文和企业常用配置。</p>
    </div>
    <div class="page-actions">
      <el-button :icon="RefreshCw" :loading="loading" @click="load">刷新数据</el-button>
    </div>
  </div>

  <div class="page-content overview-page">
    <div v-if="error" class="error-banner"><AlertTriangle :size="15" /><span>{{ error }}</span></div>

    <section class="overview-hero">
      <div class="overview-hero-copy">
        <h2>让每一次服务，<br><span>都成为下一次的经验。</span></h2>
        <p>连接知识、话术与对话记忆。遇到不确定的问题，先向导师核实，再把答案沉淀给下一次服务。</p>
        <div class="overview-hero-actions">
          <el-button type="primary" @click="router.push('/agents')">配置 AI 客服 <ArrowRight :size="15" /></el-button>
          <button class="overview-text-link" @click="router.push('/conversations')">查看对话记录 <ArrowRight :size="14" /></button>
        </div>
      </div>
      <div class="overview-art" aria-hidden="true">
        <span class="overview-orbit overview-orbit-one"></span>
        <span class="overview-orbit overview-orbit-two"></span>
        <span class="overview-float overview-float-database"><Database :size="22" /></span>
        <span class="overview-float overview-float-message"><MessageSquareText :size="22" /></span>
        <article class="overview-context-card">
          <header>
            <span class="overview-art-icon"><Bot :size="18" /></span>
            <span><strong>当前服务上下文</strong><small>正在整理回答依据</small></span>
            <i>在线</i>
          </header>
          <div class="overview-art-question">
            <span>客户</span>
            <p>之前那台旧设备，也能接新接口吗？</p>
          </div>
          <div class="overview-art-track">
            <span><Database :size="15" />知识</span><i></i>
            <span><ContactRound :size="15" />导师</span><i></i>
            <span><Activity :size="15" />记忆</span>
          </div>
          <div class="overview-art-result"><Check :size="15" /><span>已找到可复核答案，并继续回复客户</span></div>
        </article>
        <span class="overview-art-caption"><i></i>经验已沉淀，下次可直接回答</span>
      </div>
    </section>

    <section class="overview-section">
      <div class="overview-section-title">
        <div><h2>客服数据</h2><p>当前企业空间的实时运行数据</p></div>
      </div>
      <div class="overview-stat-band">
        <article v-for="metric in metrics" :key="metric.label" class="overview-stat">
          <div class="overview-stat-label"><span>{{ metric.label }}</span><component :is="metric.icon" :size="17" /></div>
          <strong>{{ loading ? '…' : metric.value }}</strong>
          <p>{{ metric.note }}</p>
        </article>
      </div>
    </section>

    <section class="overview-section">
      <div class="overview-section-title">
        <div><h2>近期服务</h2><p>查看最近读取的客户上下文与消息趋势</p></div>
        <button class="overview-text-link" @click="router.push('/conversations')">全部记录 <ArrowRight :size="14" /></button>
      </div>
      <div class="overview-service-surface">
        <div class="overview-record-block">
          <div class="overview-block-heading">
            <div><h3>客户上下文记录</h3><p>只读数据，不在工作台内创建或发送消息。</p></div>
          </div>
          <div v-if="loading && !contexts.length" class="empty-state"><span>正在读取记录…</span></div>
          <div v-else-if="!contexts.length" class="empty-state"><MessageSquareText :size="22" /><strong>暂无客户上下文</strong><span>客户开始咨询后，最近的对话和问题会显示在这里。</span></div>
          <div v-else class="table-scroll">
            <table class="data-table">
              <thead><tr><th>客服</th><th>客户</th><th>最近内容</th><th>时间</th></tr></thead>
              <tbody>
                <tr v-for="row in contexts" :key="String(row.id)">
                  <td class="mono">{{ row.agent_id ?? '—' }}</td>
                  <td class="mono">{{ row.customer_id ?? '—' }}</td>
                  <td><div class="row-sub">{{ row.content ?? row.last_message ?? '—' }}</div></td>
                  <td>{{ row.created_at ?? row.updated_at ?? '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="overview-chart-block">
          <div class="overview-block-heading">
            <div><h3>近 7 日客户消息</h3><p>按日统计已经记录的客户消息条数。</p></div>
            <Activity :size="18" class="muted" />
          </div>
          <div v-if="trend.length" class="overview-chart">
            <div class="chart-legend"><i></i>客户消息</div>
            <div class="chart-bars">
              <div v-for="item in trend" :key="item.label" class="chart-col">
                <span class="chart-bar" :style="{ '--bar-height': `${Math.max(4, item.value / maxTrend * 100)}%` }"></span>
                <span class="chart-label">{{ item.label }}</span>
              </div>
            </div>
          </div>
          <div v-else class="empty-state overview-chart-empty">
            <Database :size="22" />
            <strong>{{ loading ? '正在读取统计…' : '近 7 日暂无客户消息' }}</strong>
            <span>这里统计客户实际发来的消息条数。</span>
          </div>
        </div>
      </div>
    </section>

    <section class="overview-section">
      <div class="overview-section-title">
        <div><h2>管理入口</h2><p>从常用配置开始维护客服能力</p></div>
        <button class="overview-text-link" @click="router.push('/settings')"><Settings2 :size="14" />企业设置</button>
      </div>
      <div class="overview-command-grid">
        <button v-for="item in shortcuts" :key="item.path" class="overview-command" @click="router.push(item.path)">
          <span class="overview-command-icon"><component :is="item.icon" :size="18" /></span>
          <span class="overview-command-copy"><strong>{{ item.title }}</strong><small>{{ item.note }}</small></span>
          <ArrowRight :size="15" class="overview-command-arrow" />
        </button>
      </div>
    </section>
  </div>
</template>
